import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Box,
  Button,
  CollectionPreferences,
  Header,
  Pagination,
  StatusIndicator,
  Table,
  TableProps,
  TextFilter,
} from "@amzn/awsui-components-console";
import React, { useMemo, useState } from "react";

import { AnalyticsUserRow } from "@/hooks/admin/useAdminUserAnalytics";
import {
  ADMIN_DEFAULT_PAGE_SIZE,
  adminPageSizePreference,
} from "@/utils/adminTable";

import EmptyState from "../EmptyState";
import {
  formatCount,
  formatDurationSeconds,
  formatTokens,
  formatUsd,
} from "./format";

interface UserUsageTableProps {
  rows: AnalyticsUserRow[];
  loading?: boolean;
}

/**
 * Per-user usage for the selected window, sortable and filterable.
 *
 * Users appear here because they asked something. Someone who only opened a
 * connection without asking is counted in the session totals but is not listed
 * as an active user, so this table can be shorter than the session count
 * suggests.
 */
const UserUsageTable: React.FC<UserUsageTableProps> = ({
  rows,
  loading = false,
}) => {
  // Rows per page, adjustable via the table's Preferences. State, not a ref:
  // the value is rendered — it decides how many rows appear — so the re-render
  // on change is the point.
  const [preferences, setPreferences] = useState<{ pageSize: number }>({
    pageSize: ADMIN_DEFAULT_PAGE_SIZE,
  });

  const columnDefinitions = useMemo<
    TableProps<AnalyticsUserRow>["columnDefinitions"]
  >(
    () => [
      {
        id: "displayName",
        header: "User",
        cell: (row) => <Box variant="strong">{row.displayName}</Box>,
        sortingField: "displayName",
      },
      {
        id: "questions",
        header: "Questions",
        cell: (row) => formatCount(row.questions),
        sortingField: "questions",
      },
      {
        id: "sessions",
        header: "Sessions",
        cell: (row) => formatCount(row.sessions),
        sortingField: "sessions",
      },
      {
        id: "avgSessionSeconds",
        header: "Avg session",
        // Null here means no session of theirs was observed end to end, which
        // is not a session of zero length.
        cell: (row) => formatDurationSeconds(row.avgSessionSeconds),
        sortingField: "avgSessionSeconds",
      },
      {
        id: "totalTokens",
        header: "Tokens",
        cell: (row) => formatTokens(row.totalTokens),
        sortingField: "totalTokens",
      },
      {
        id: "estimatedCostUsd",
        header: "Estimated cost",
        cell: (row) =>
          row.costIsPartial ? (
            <StatusIndicator type="warning" colorOverride="grey">
              {`${formatUsd(row.estimatedCostUsd)} (partial)`}
            </StatusIndicator>
          ) : (
            formatUsd(row.estimatedCostUsd)
          ),
        sortingField: "estimatedCostUsd",
      },
    ],
    []
  );

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(rows, {
    filtering: {
      // Both of these reach the table through `collectionProps`, so the Table
      // below must not also pass an `empty` of its own: an explicit prop after
      // the spread shadows them, and the filtered case would then claim nobody
      // asked anything when the truth is that nobody matched the search.
      empty: (
        <EmptyState
          title="No user activity"
          subtitle="Nobody asked a question in this period."
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="No user matches that search."
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
      filteringFunction: (row: AnalyticsUserRow, text: string) => {
        const query = text.toLowerCase();
        return (
          row.displayName.toLowerCase().includes(query) ||
          row.userId.toLowerCase().includes(query)
        );
      },
    },
    pagination: { pageSize: preferences.pageSize },
    // Order by the name a reader is scanning for, ascending. Usage numbers stay
    // one header click away.
    sorting: {
      defaultState: { sortingColumn: { sortingField: "displayName" } },
    },
  });

  return (
    <Table
      {...collectionProps}
      data-testid="user-usage-table"
      variant="container"
      columnDefinitions={columnDefinitions}
      items={items}
      loading={loading}
      loadingText="Loading user activity"
      trackBy="userId"
      header={
        <Header variant="h2" counter={`(${rows.length})`}>
          Activity by user
        </Header>
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Search by name"
          countText={
            filteredItemsCount !== undefined
              ? `${filteredItemsCount} matches`
              : ""
          }
        />
      }
      pagination={<Pagination {...paginationProps} />}
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={preferences}
          pageSizePreference={adminPageSizePreference()}
          onConfirm={({ detail }) =>
            setPreferences({
              pageSize: detail.pageSize ?? ADMIN_DEFAULT_PAGE_SIZE,
            })
          }
        />
      }
    />
  );
};

export default UserUsageTable;
